import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MarkdownContent } from "@/components/ui/markdown-content";
import { cn } from "@/lib/cn";
import {
  handwrittenCalloutPlacement,
  NOTE_BRACKET_WIDTH_PX,
  type NoteRect,
} from "@/lib/handwritten-callout-placement";
import { HandwrittenNoteBracket } from "./handwritten-note-bracket";
import { usePointerDragOffset } from "./use-pointer-drag-offset";
import { useSketchReveal } from "./use-sketch-reveal";

const BRACKET_REVEAL_MS = 180;
const LARGE_FONT_PX = 22;
const SMALL_FONT_PX = 19;
const LINE_HEIGHT = 1.1;
/** Notes longer than this many lines drop to the small size. */
const MAX_LARGE_LINES = 6;
const NIB_DOT_PX = 5;
const VIEWPORT_INSET_PX = 8;

type Point = { x: number; y: number };

interface HandwrittenCalloutProps {
  annotationId?: string;
  content: string;
  role?: "context" | "answer";
  ink: string;
  /** Wobble seed of the mark; the bracket derives its own from it. */
  seed: string;
  /** Display-local content box of the mark. */
  mark: NoteRect;
  /** An arrow's note sits beyond its tail. */
  arrow?: { tail: Point; tip: Point };
  /** Display-local bounds of the note, for the mark's shared hover. */
  onBoundsChange?: (bounds: NoteRect | null) => void;
  testId: string;
}

function textRevealMs(lineWidth: number): number {
  return Math.min(1600, Math.max(500, lineWidth * 4));
}

/** A handwritten note beside an agent mark: a pen-drawn bracket, then the
 * text writing itself left to right. The full text is in the DOM from mount.
 * Answer notes can be dragged; context notes are click-through. */
export function HandwrittenCallout({
  annotationId,
  content,
  role,
  ink,
  seed,
  mark,
  arrow,
  onBoundsChange,
  testId,
}: HandwrittenCalloutProps) {
  const answer = role === "answer";
  const textRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [smallFor, setSmallFor] = useState<string | null>(null);
  const small = smallFor === content;
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Drags continue from the clamped offset, so pushing past an edge leaves
  // no dead zone on the way back.
  const appliedOffsetRef = useRef(offset);
  const { handlers: dragHandlers } = usePointerDragOffset(answer, (dx, dy) => {
    appliedOffsetRef.current = {
      x: appliedOffsetRef.current.x + dx,
      y: appliedOffsetRef.current.y + dy,
    };
    setOffset(appliedOffsetRef.current);
  });

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;
    const measure = () => {
      const width = element.offsetWidth;
      const height = element.offsetHeight;
      setSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );
      const lines = element.scrollHeight / (LARGE_FONT_PX * LINE_HEIGHT);
      if (lines > MAX_LARGE_LINES + 0.5) setSmallFor(content);
    };
    measure();
    // Also fires once the bundled handwriting font has loaded.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [content]);

  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const placement = handwrittenCalloutPlacement(
    { mark, arrow },
    size,
    viewport,
  );
  const clampOffset = (
    value: number,
    base: number,
    extent: number,
    max: number,
  ) =>
    Math.min(
      Math.max(base + value, VIEWPORT_INSET_PX),
      Math.max(VIEWPORT_INSET_PX, max - VIEWPORT_INSET_PX - extent),
    ) - base;
  const dx = clampOffset(offset.x, placement.left, size.width, viewport.width);
  const dy = clampOffset(offset.y, placement.top, size.height, viewport.height);
  appliedOffsetRef.current = { x: dx, y: dy };
  const left = placement.left + dx;
  const top = placement.top + dy;
  const bracket = placement.bracket;

  const revealKey = `${seed}:${content}`;
  const bracketProgress = useSketchReveal({
    key: `${revealKey}:bracket`,
    durationMs: bracket ? BRACKET_REVEAL_MS : 0,
    enabled: true,
  });
  const bracketDrawn = bracketProgress >= 1;
  // The pace is fixed once the pen starts writing, so a late font load that
  // re-measures the line does not restart the reveal.
  const [textPace, setTextPace] = useState<{ key: string; ms: number } | null>(
    null,
  );
  const textMs =
    textPace?.key === revealKey ? textPace.ms : textRevealMs(size.width);
  const writtenProgress = useSketchReveal({
    key: `${revealKey}:text`,
    durationMs: textMs,
    enabled: bracketDrawn,
  });
  if (bracketDrawn && writtenProgress > 0 && textPace?.key !== revealKey) {
    setTextPace({ key: revealKey, ms: textMs });
  }
  const textProgress = bracketDrawn ? writtenProgress : 0;

  // The note's box joined with its bracket, which moves with it.
  const boundsLeft = bracket ? Math.min(left, bracket.left + dx) : left;
  const boundsTop = bracket ? Math.min(top, bracket.top + dy) : top;
  const right = Math.max(
    left + size.width,
    bracket ? bracket.left + dx + NOTE_BRACKET_WIDTH_PX : -Infinity,
  );
  const bottom = Math.max(
    top + size.height,
    bracket ? bracket.top + dy + bracket.height : -Infinity,
  );
  const onBoundsChangeRef = useRef(onBoundsChange);
  onBoundsChangeRef.current = onBoundsChange;
  useEffect(() => {
    onBoundsChangeRef.current?.({
      left: boundsLeft,
      top: boundsTop,
      right,
      bottom,
    });
  }, [boundsLeft, boundsTop, right, bottom]);
  useEffect(() => () => onBoundsChangeRef.current?.(null), []);

  const writing = textProgress > 0 && textProgress < 1;
  return (
    <div
      className="pointer-events-none fixed z-[9999] w-max"
      data-testid={`${testId}-callout-anchor`}
      data-annotation-callout-id={annotationId}
      style={{ left, top }}
    >
      {bracket ? (
        <HandwrittenNoteBracket
          side={bracket.side}
          height={bracket.height}
          left={bracket.left - placement.left}
          top={bracket.top - placement.top}
          progress={bracketProgress}
          ink={ink}
          seed={seed}
          testId={testId}
        />
      ) : null}
      <div
        ref={textRef}
        className={cn(
          "w-max break-words font-handwriting [overflow-wrap:anywhere] [&_*]:text-inherit",
          answer
            ? "pointer-events-auto cursor-grab touch-none overflow-y-auto data-[dragging=true]:cursor-grabbing"
            : "pointer-events-none overflow-hidden",
        )}
        style={{
          color: ink,
          fontSize: small ? SMALL_FONT_PX : LARGE_FONT_PX,
          lineHeight: LINE_HEIGHT,
          maxWidth: "min(300px, 40vw)",
          maxHeight: "45vh",
          clipPath: `inset(0 ${Number(((1 - textProgress) * 100).toFixed(2))}% 0 0)`,
        }}
        data-agent-annotation-callout="true"
        data-cursor-reply={content}
        data-callout-role={role ?? "context"}
        data-interactive={answer ? "true" : undefined}
        data-draggable={answer ? "true" : undefined}
        data-testid={`${testId}-callout`}
        {...(answer ? dragHandlers : {})}
      >
        <MarkdownContent
          content={content}
          className="[&_li]:leading-[inherit] [&_ol]:pl-5 [&_ul]:pl-4"
        />
      </div>
      {writing ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute rounded-full"
          style={{
            width: NIB_DOT_PX,
            height: NIB_DOT_PX,
            left: size.width * textProgress - NIB_DOT_PX / 2,
            top:
              size.height * (0.35 + 0.3 * Math.sin(textProgress * 18)) -
              NIB_DOT_PX / 2,
            background: ink,
            opacity: Math.min(1, (1 - textProgress) * 4),
          }}
          data-testid={`${testId}-callout-nib`}
        />
      ) : null}
    </div>
  );
}
